import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Loading from "../../../../components/Loading/Loading";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import { useToggle } from "../../../../hooks/useToggle";
import useCurrency from "../../../../hooks/useCurrency";
import useOrderDetails from "./hooks/useOrderDetails";
import "./OrderDetails.css";
import OrderDetailsHeaderSection from "./OrderDetailsHeaderSection";
import OrderDetailsMainGrid from "./OrderDetailsMainGrid";
import OrderDetailsShippingInfoCard from "./OrderDetailsShippingInfoCard";
import OrderDetailsSellerPickupPanel from "./OrderDetailsSellerPickupPanel";
import OrderDetailsBuyerInfoPanel from "./OrderDetailsBuyerInfoPanel";
import OrderDetailsDialogs from "./OrderDetailsDialogs";
import OrderDetailsLockedCard from "./OrderDetailsLockedCard";

const OrderDetails = (props) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const {
    isLoading: loading,
    loadingLabel,
    confirmReceivedVisible,
    links,
    currentOrder,
    parcelInfo,
    confirmSendVisible,
    currency,
    shippingCompany,
    trackingObject,
    setConfirmSendVisible,
    setConfirmReceivedVisible,
    setShippingCompany,
    setTrackingObject,
    downloadLabel,
    sendOrder,
    receiveOrder,
    isReadyToPickup,
    isLoadingReadyForPickup,
  } = useOrderDetails({
    admin: props?.admin ? props?.admin : false,
    sales: props.sales,
    onErrorReceiveOrder: (code) => showErrorToast({ code }),
    onErrorSendOrder: (code) => showErrorToast({ code }),
    onErrorFillOutAll: () => showErrorToast({ detail: "pleaseFillOutAll" }),
    onErrorDownloadProducts: (code) => showErrorToast({ code }),
    onErrorGetParcel: () => showErrorToast({ code: 500000 }),
    onSuccessSendOrder: () => showSuccessToast({ detail: "productSentSuccessfully" }),
    onSuccessReceiveOrder: () => showSuccessToast({ detail: "productReceivedSuccessfully" }),
    onSuccessOrderIsReadyToPickup: () => showSuccessToast({ detail: "productIsReadyToPickup" }),
  });

  const { currencySymbol } = useCurrency(currency);
  const History = useHistory();
  const url = useLocation();
  const [params, setParams] = useState(new URLSearchParams(window?.location?.search));

  const {
    value: isVisibleChat,
    setAsTrue: setVisibleChat,
    setAsFalse: setNotVisibleChat,
  } = useToggle();

  const handleOpenChat = (e) => {
    e.stopPropagation();
    if (currentOrder?.status !== "PENDING") {
      const newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?chat=true";

      window.history.pushState({ path: newurl }, "", newurl);
      setVisibleChat();
    }
  };

  const handleCloseChat = () => {
    const newurl =
      window.location.protocol + "//" + window.location.host + window.location.pathname;

    window.history.pushState({ path: newurl }, "", newurl);
    setParams(new URLSearchParams(newurl));
    setNotVisibleChat();
  };

  const handleGoBack = () => {
    let urlToBack;
    if (props?.storeBackUrl) {
      urlToBack = props.storeBackUrl;
    } else if (props?.admin) {
      urlToBack = "/dashboard-listorders";
    } else {
      urlToBack = url?.pathname?.includes("sales") ? "/dashboard-sales" : "/dashboard-purchases";
    }
    History.push(urlToBack);
  };

  useEffect(() => {
    if (currentOrder?.status !== "PENDING" && params.get("chat") === "true") setVisibleChat();
  }, [currentOrder, params, setVisibleChat, setNotVisibleChat]);

  const isSeller =
    currentOrder?.seller?.username === localStorage.getItem("__extraconomy_username");

  const isPhysicalProduct =
    !currentOrder?.itens?.[0]?.product?.service && !currentOrder?.itens?.[0]?.product?.downloadable;

  const status = currentOrder?.status;

  const canReadyForPickup =
    !props.admin &&
    props.sales &&
    status === "PAID" &&
    currentOrder?.pickinaddress &&
    isPhysicalProduct &&
    !parcelInfo;

  const canChangeStatusToSentPhysical =
    !props.admin &&
    props.sales &&
    status === "PAID" &&
    isPhysicalProduct &&
    !currentOrder?.pickinaddress &&
    ((currentOrder?.buyer?.country !== "IT" && currentOrder?.seller?.country !== "IT") ||
      currentOrder?.shippingOption === "FREE") &&
    currentOrder?.banggood_order === false;

  const canChangeStatusToSentService =
    !props.admin &&
    props.sales &&
    status === "PAID" &&
    currentOrder?.itens?.[0]?.product?.subCategory?.service &&
    currentOrder?.banggood_order === false;

  const canChangeStatusToSent = canChangeStatusToSentPhysical || canChangeStatusToSentService;

  const canReceiveOrderButton =
    !props.admin &&
    !props.sales &&
    status === "SENT" &&
    !currentOrder?.itens?.[0]?.product?.downloadable;

  const canReceiveDownloadLinksButton =
    !props.admin &&
    !props.sales &&
    status === "SENT" &&
    currentOrder?.itens?.[0]?.product?.downloadable;

  return (
    <>
      {props.sales && props.plan === "Promoter" ? (
        <OrderDetailsLockedCard />
      ) : (
        <div>
          <OrderDetailsDialogs
            loading={loading}
            confirmReceivedVisible={confirmReceivedVisible}
            confirmSendVisible={confirmSendVisible}
            currentOrder={currentOrder}
            parcelInfo={parcelInfo}
            shippingCompany={shippingCompany}
            trackingObject={trackingObject}
            setConfirmReceivedVisible={setConfirmReceivedVisible}
            setConfirmSendVisible={setConfirmSendVisible}
            setShippingCompany={setShippingCompany}
            setTrackingObject={setTrackingObject}
            receiveOrder={receiveOrder}
            sendOrder={sendOrder}
            isVisibleChat={isVisibleChat}
            onCloseChat={handleCloseChat}
          />

          {loading ? (
            <Loading />
          ) : (
            <>
              {currentOrder && (
                <div className="mt-4">
                  <OrderDetailsHeaderSection
                    currentOrder={currentOrder}
                    admin={props.admin}
                    sales={props.sales}
                    isSeller={isSeller}
                    status={status}
                    hasStoreBackUrl={!!props.storeBackUrl}
                    canReadyForPickup={canReadyForPickup}
                    canChangeStatusToSent={canChangeStatusToSent}
                    canReceiveOrderButton={canReceiveOrderButton}
                    canReceiveDownloadLinksButton={canReceiveDownloadLinksButton}
                    isLoadingReadyForPickup={isLoadingReadyForPickup}
                    onGoBack={handleGoBack}
                    onOpenChat={(e) => handleOpenChat(e)}
                    onReadyForPickup={isReadyToPickup}
                    onChangeStatusToSent={() => setConfirmSendVisible(true)}
                    onReceiveOrder={() => setConfirmReceivedVisible(true)}
                    onReceiveDownloadLinks={() =>
                      receiveOrder(currentOrder.buyer.username, currentOrder.id)
                    }
                  />
                  <OrderDetailsMainGrid
                    currentOrder={currentOrder}
                    currencySymbol={currencySymbol}
                    links={links}
                    sales={props.sales}
                    isSeller={isSeller}
                  />
                  <OrderDetailsShippingInfoCard currentOrder={currentOrder} />
                  {!props.admin && props.sales && (
                    <OrderDetailsSellerPickupPanel
                      isPhysicalProduct={isPhysicalProduct}
                      parcelInfo={parcelInfo}
                      loadingLabel={loadingLabel}
                      downloadLabel={downloadLabel}
                    />
                  )}
                  {!props.admin && !props.sales && (
                    <OrderDetailsBuyerInfoPanel
                      status={status}
                      currentOrder={currentOrder}
                      isPhysicalProduct={isPhysicalProduct}
                      parcelInfo={parcelInfo}
                      sales={props.sales}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default OrderDetails;
